import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Grid,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import Custom3dotString from "../../../components/Custom3dotString";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTable from "../../../components/CustomTable";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import { getStockAdjust } from "../../../redux/reducers/clinicSlice";
import { AppColors } from "../../../util/AppColors";
import { StockAdjustList } from "../../../util/arrayList";
import { plusBtn } from "../../../util/CommonIconStyle";
import { tblGrayBack } from "../../../util/object";
import { canWriteModule } from "../../../util/roleModuleAccess";
import StockAdjustment from "../../CommonScreens/Stock/StockAdjustment";

const tblHdrs = [
  "adjustId",
  "type",
  "remarks",
  "supplierCustomer",
  "date",
  "details",
  "view",
];
const viewTblHdrs = [
  "product",
  "adjustedQuantity",
  "uom",
  "batchNumber",
  "location",
];
const nameExpan = {
  adjustId: "#",
  type: "Type",
  remarks: "Reference / Reason",
  supplierCustomer: "Supplier/Customer Name",
  date: "Date",
  details: "Details",
  view: "",
};
const initSearch = {
  startDate: null,
  endDate: null,
  customerName: "",
  supplierName: "",
  invoiceNumber: "",
};

const AdjustmentHistory = () => {
  const dispatch = useDispatch();
  const canManageInventory = canWriteModule("inventory");
  const fetchRef = useRef(false);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [modVsble, setModVsble] = useState(false);
  const [modValues, setModValues] = useState({});
  const [viewModVsble, setViewModVsble] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [page, setPage] = useState(1);
  const [adjustModVsble, setAdjustModVsble] = useState(false);
  const itemSize = 10;
  const { stockAdjust } = useSelector((state) => state?.clinic);
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { stocks: clinicStocks } = useMemo(() => {
    if (!mandateDataRaw) {
      return { stocks: [] };
    }
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        stocks: Array.isArray(reqData?.stocks) ? reqData?.stocks : [],
      };
    } catch {
      return { stocks: [] };
    }
  }, [mandateDataRaw]);

  useEffect(() => {
    onApiCall(1);
  }, [
    searchValues?.startDate,
    searchValues?.endDate,
    searchValues?.customerName,
    searchValues?.supplierName,
    searchValues?.invoiceNumber,
    selectedFilter,
  ]);

  const getResolvedDateRange = () => {
    const startRaw = searchValues?.startDate;
    const endRaw = searchValues?.endDate;
    if (!startRaw && !endRaw) return { startDate: null, endDate: null };

    const start = startRaw ? moment(new Date(startRaw)) : null;
    let end = endRaw ? moment(new Date(endRaw)) : null;

    // If endDate is missing, use startDate
    if (start && !end) end = start.clone();
    // If only endDate exists, use it for both
    if (!start && end) {
      return {
        startDate: end.format("YYYY-MM-DD"),
        endDate: end.format("YYYY-MM-DD"),
      };
    }
    // If endDate is earlier than startDate, swap
    if (start && end && end.isBefore(start, "day")) {
      return {
        startDate: end.format("YYYY-MM-DD"),
        endDate: start.format("YYYY-MM-DD"),
      };
    }

    return {
      startDate: start?.format("YYYY-MM-DD") ?? null,
      endDate: end?.format("YYYY-MM-DD") ?? null,
    };
  };

  const onApiCall = (pge = 1) => {
    setPage(pge);
    const { startDate, endDate } = getResolvedDateRange();
    let reqUrl = `?page=${pge}&itemSize=${itemSize}&approve=Y`;
    if (startDate) reqUrl += `&startDate=${startDate}`;
    if (endDate) reqUrl += `&endDate=${endDate}`;
    if (searchValues?.supplierName?.length > 0) {
      reqUrl += `&supplierName=${searchValues?.supplierName}`;
    }
    if (searchValues?.customerName?.length > 0) {
      reqUrl += `&customerName=${searchValues?.customerName}`;
    }
    if (searchValues?.invoiceNumber?.length > 0) {
      reqUrl += `&invoiceNumber=${searchValues?.invoiceNumber}`;
    }
    if (selectedFilter?.value) reqUrl += `&type=${selectedFilter?.value}`;
    dispatch(getStockAdjust(reqUrl));
  };

  const tableList = () => {
    const reqList = (stockAdjust?.data ?? [])?.map((li) => {
      const reqQty = (li?.stockAdjustDetails ?? [])?.reduce((acc, obj) => {
        return acc + (Number(obj?.quantity) ?? 0);
      }, 0);
      return {
        ...li,
        date: moment(new Date(li?.createdDate)).format("DD MMM YYYY"),
        supplierCustomer: li?.supplierName ?? li?.customerName ?? "",
        details: `${Math.abs(reqQty)} items adjusted`,
      };
    });
    return reqList;
  };

  const onCloseMod = (typ) => {
    setAdjustModVsble(false);
    if (typ === "apiSuccess") onApiCall();
  };

  const onSelectInvoice = (inv) => {
    setSelectedInvoice(inv);
    setModVsble(true);
  };

  const handleChangePage = (e, selectedPage) => {
    onApiCall(selectedPage);
  };

  const handleCreate = () => {
    setAdjustModVsble(true);
  };

  const getUOMListByStockId = (stkId = null) => {
    if (!stkId) return [];
    const stockMat = clinicStocks?.find((stc) => stc?.stockId === stkId);
    if (!stockMat) return [];
    const reqList = stockMat?.uomMappings
      ?.filter((ml) => ml?.allowSale === "Y" && ml?.show)
      ?.map((ml) => ({
        name: ml?.unit,
        value: ml?.unit,
        uomMappingId: ml?.uomMappingId ?? null,
      }));
    return reqList;
  };

  const handleView = (itm) => {
    setSelectedItem(itm);
    setViewModVsble(true);
  };

  const onCloseViewMod = () => {
    setViewModVsble(false);
    setSelectedItem(null);
  };

  const getTableList = () => {
    if (!selectedItem) return [];
    const reqList = (selectedItem?.stockAdjustDetails ?? [])?.map((li) => ({
      product: li?.productName ?? "",
      adjustedQuantity: li?.quantity ?? "",
      uom: li?.uomName ?? "",
      batchNumber: li?.batchNumber ?? "",
      location: li?.locationName ?? "",
    }));
    return reqList;
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({
      ...searchValues,
      [name?.trim()?.replace(/,$/, "")]: value,
    });
  };

  const onReset = () => {
    flushSync(() => {
      setSearchValues(initSearch);
      setSelectedFilter(null);
    });
  };

  return (
    <>
      <div className="com-mar back-white pv10">
        <div className="flex-row-ali-cen w100Per">
          <div className="flex-center w100Per">
            <div className="w13Per">
              <CustomTextField
                placeholder="Start Date"
                fullWidth
                name="startDate"
                handleChange={onSearchChange}
                value={searchValues?.startDate}
                backgroundColor={AppColors.lightBlue2}
                label="Start Date"
                mobileDate
              />
            </div>
            <div className="w13Per ml10">
              <CustomTextField
                placeholder="End Date"
                fullWidth
                name="endDate"
                handleChange={onSearchChange}
                value={searchValues?.endDate}
                backgroundColor={AppColors.lightBlue2}
                label="End Date"
                mobileDate
              />
            </div>
            <div className="w13Per ml10">
              <CustomTextField
                placeholder="Supplier"
                fullWidth
                name="supplierName"
                handleChange={onSearchChange}
                value={searchValues?.supplierName}
                backgroundColor={AppColors.lightBlue2}
                label="Supplier"
              />
            </div>
            <div className="w13Per ml10">
              <CustomTextField
                placeholder="Customer"
                fullWidth
                name="customerName"
                handleChange={onSearchChange}
                value={searchValues?.customerName}
                backgroundColor={AppColors.lightBlue2}
                label="Customer"
              />
            </div>
            <div className="w13Per ml10">
              <CustomTextField
                placeholder="Invoice Number"
                fullWidth
                name="invoiceNumber"
                handleChange={onSearchChange}
                value={searchValues?.invoiceNumber}
                backgroundColor={AppColors.lightBlue2}
                label="Invoice Number"
              />
            </div>
            <div className="w13Per ml10">
              <CustomSelect
                list={[
                  ...StockAdjustList,
                  { name: "Return on Sales", value: "sales" },
                ]}
                fullWidth
                value={selectedFilter}
                handleChange={(e, value) => setSelectedFilter(value)}
                search
                label="Type"
                backgroundColor={AppColors.lightBlue2}
              />
            </div>
            <div className="ml10">
              <CustomButton text="Reset" smallBtn onClick={onReset} redBtn />
            </div>
            {canManageInventory ? (
              <div className="ml10">
                <CustomButton text="Create" smallBtn onClick={handleCreate} />
              </div>
            ) : null}
          </div>
        </div>

        <div className="ml20">
          <Typography className="font-medium fs12 gray28 mt5">
            Number of count: {stockAdjust?.totalRecords ?? 0}
          </Typography>
        </div>
        <Box className="ph20">
          <TableContainer sx={{ maxHeight: 470 }} className="mb20 mt10">
            <Table
              sx={{ minWidth: 200 }}
              aria-labelledby="tableTitle"
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  {tblHdrs?.map((hdr, i) => (
                    <TableCell
                      style={tblGrayBack}
                      className="txt-semi-bold fs12"
                      key={hdr + i}
                    >
                      <div>{nameExpan?.[hdr]}</div>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {tableList()?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} className="text-align-center h100">
                    <div className="no-rec">No records available</div>
                  </TableCell>
                </TableRow>
              )}
              <TableBody>
                {tableList()?.map((li, ind) => {
                  return (
                    <TableRow key={`adj_${ind}_${li?.adjustId ?? ind}`}>
                      {tblHdrs?.map((hdr, i) => {
                        return (
                          <TableCell key={`${ind} + ${i} + tr`} component="th">
                            <div>
                              {hdr === "supplier" ? (
                                <Custom3dotString
                                  str={li?.[hdr]}
                                  className="font-regular fs14"
                                />
                              ) : hdr === "invoice" ? (
                                <div
                                  className="cursor"
                                  onClick={() => onSelectInvoice(li)}
                                >
                                  <Custom3dotString
                                    str={li?.[hdr]}
                                    className="font-regular fs14 blue4"
                                  />
                                </div>
                              ) : hdr === "view" ? (
                                <div
                                  className="cursor"
                                  onClick={() => handleView(li)}
                                >
                                  <div className="flex-center">
                                    <VisibilityIcon sx={plusBtn} />
                                  </div>
                                  <Typography className="blue-color fs8 txt-semi-bold text-align-center">
                                    View
                                  </Typography>
                                </div>
                              ) : (
                                li?.[hdr]
                              )}
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
              {Math.ceil((stockAdjust?.totalRecords ?? 0) / itemSize) > 1 ? (
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={12}>
                      <div className="flex-end">
                        <Pagination
                          count={Math.ceil(
                            (stockAdjust?.totalRecords ?? 0) / itemSize,
                          )}
                          variant="outlined"
                          color="primary"
                          page={page}
                          onChange={handleChangePage}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              ) : null}
            </Table>
          </TableContainer>
        </Box>
      </div>

      {adjustModVsble ? (
        <StockAdjustment modVsble={adjustModVsble} onCloseMod={onCloseMod} />
      ) : null}

      <CustomModal
        open={viewModVsble}
        onClose={onCloseViewMod}
        header="Stock Adjustment"
        headerCenter
        modalWidth={90}
        modal
      >
        <div className="scroll-80vh">
          <Grid container spacing={2} className="mt5 ph20">
            <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
              <CustomTextField
                label="Type"
                disabled
                fullWidth
                value={selectedItem?.type ?? ""}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
              <CustomTextField
                label="Created Date"
                disabled
                fullWidth
                mobileDate
                value={selectedItem?.createdDate}
              />
            </Grid>
            {["sales", "return on purchase"].includes(
              selectedItem?.type?.toLowerCase(),
            ) ? (
              <>
                <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                  <CustomTextField
                    label="Invoice Number"
                    disabled
                    fullWidth
                    value={selectedItem?.invoiceNumber ?? ""}
                  />
                </Grid>
                {(
                  selectedItem?.type?.toLowerCase() === "sales"
                    ? selectedItem?.invoiceDate
                    : selectedItem?.billDate
                ) ? (
                  <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                    <CustomTextField
                      label="Invoice Date"
                      disabled
                      fullWidth
                      mobileDate
                      value={
                        selectedItem?.type?.toLowerCase() === "sales"
                          ? selectedItem?.invoiceDate
                          : selectedItem?.billDate
                      }
                    />
                  </Grid>
                ) : null}
                <Grid item xs={6} sm={6} md={3} lg={3} xl={3}>
                  <CustomTextField
                    label={`${selectedItem?.type?.toLowerCase() === "sales" ? "Customer" : "Supplier"} Name`}
                    disabled
                    fullWidth
                    value={
                      (selectedItem?.type?.toLowerCase() === "sales"
                        ? selectedItem?.customerName
                        : selectedItem?.supplierName) ?? ""
                    }
                  />
                </Grid>
              </>
            ) : null}
            <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
              <CustomTextField
                label="Remarks"
                disabled
                fullWidth
                value={selectedItem?.remarks}
              />
            </Grid>
          </Grid>
          <CustomTable columns={viewTblHdrs} datas={getTableList()} grey />
        </div>
      </CustomModal>
    </>
  );
};

export default AdjustmentHistory;
